/**
 * English (en) UI dictionary - the default locale.
 *
 * Native, conversion-focused English copy. Must satisfy the {@link Dictionary}
 * shape derived from the German dictionary, so TypeScript flags any missing or
 * extra key the moment one locale drifts from the other.
 */
import type { Dictionary } from './de'

export const en = {
  common: {
    signUpCta: 'Start for free',
    dashboardCta: 'Dashboard',
    login: 'Log in',
    signOut: 'Sign out',
    backToSite: 'Back to site',
    account: 'Account',
    allCourses: 'All courses',
    viewAll: 'View all',
    learnMore: 'Learn more',
    loading: 'Loading...',
    sending: 'Sending...',
    backToOverview: 'Back to overview',
    breadcrumbLabel: 'Breadcrumb',
    faqLabel: 'FAQ',
  },
  // Engagement nudges shown next to a lesson/resource action once a visitor has
  // built up enough local progress to be worth saving. Keyed by reason so the
  // logic stays locale-neutral and the copy is translated per locale.
  signupPrompt: {
    saves: 'Save your progress permanently with a free account.',
    progress:
      'Keep your learning progress for free and sync it across all your devices.',
    local: 'Your progress is still saved on this device only.',
  },
  nav: {
    mainLabel: 'Main navigation',
    toolbarLabel: 'Toolbar',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    menuDescription:
      'Main menu with courses, community, resources, changelog and account.',
    search: 'Search',
    toggleTheme: 'Switch theme',
    toggleLanguage: 'Switch language',
    // The visible token in the toggle is the locale code ({code}), so the
    // accessible name must contain it to satisfy WCAG "Label in Name".
    toggleLanguageLabel: 'Switch language. Current language: {name} ({code})',
    languageName: {
      en: 'English',
      de: 'Deutsch',
      fr: 'Français',
      it: 'Italiano',
    },
    sections: {
      courses: {
        label: 'Courses',
        tagline:
          'From your first setup to a production-grade agentic workflow.',
        overview: 'All courses',
      },
      community: {
        label: 'Community',
        tagline: 'Share your workflow and get real, useful feedback.',
        overview: 'Go to community',
      },
      resources: {
        label: 'Resources',
        tagline: 'Checklists, templates and playbooks built for real work.',
        overview: 'All resources',
      },
      knowledge: {
        label: 'Knowledge',
        tagline: 'Guides and fundamentals across agentic engineering.',
        overview: 'All guides',
      },
      company: {
        label: 'Company',
        tagline: 'Meet the idea and the people behind the campus.',
        overview: 'Get in touch',
      },
      legal: {
        label: 'Legal',
        tagline: 'Full transparency on data, privacy and terms.',
        overview: 'Read the privacy policy',
      },
    },
    links: {
      pricing: 'Pricing',
      allLessons: 'All lessons',
      allTopics: 'All topics',
      topics: 'Topics',
      questions: 'Questions',
      showcases: 'Showcases',
      workflowReviews: 'Workflow reviews',
      toolUpdates: 'Tool updates',
      allResources: 'All resources',
      checklists: 'Checklists',
      fundamentals: 'Fundamentals',
      allFundamentals: 'All fundamentals',
      builds: 'Builds',
      allBuilds: 'All builds',
      glossary: 'AI glossary',
      guides: 'Guides',
      compare: 'Comparisons',
      tools: 'Free tools',
      about: 'About the founder',
      contact: 'Contact',
      newsletter: 'Newsletter',
      changelog: 'Changelog',
      account: 'Account',
      login: 'Log in',
      imprint: 'Imprint',
      privacy: 'Privacy',
      cookieSettings: 'Cookie settings',
      terms: 'Terms',
      accessibility: 'Accessibility',
    },
    mobileSections: {
      courses: 'Courses',
      community: 'Community',
      resources: 'Resources',
      company: 'Company',
      legal: 'Legal',
    },
  },
  footer: {
    tagline:
      'The public-first campus for agentic engineering in business. Workflows, APIs and tools - built for measurable ROI.',
    followUs: 'Follow us',
    legalLabel: 'Legal',
    madeIn: 'Made in Switzerland',
    builtBy: 'Built by',
    columns: {
      courses: 'Courses',
      community: 'Community',
      resources: 'Resources',
      company: 'Company',
    },
    links: {
      communityHub: 'Community hub',
      allTopics: 'All topics',
      workflowReviews: 'Workflow reviews',
      toolUpdates: 'Tool updates',
      showcases: 'Showcases',
      beginnerQuestions: 'Beginner questions',
      knowledge: 'Knowledge',
      fundamentals: 'Fundamentals',
      glossary: 'AI glossary',
      guides: 'Guides',
      compare: 'Comparisons',
      tools: 'Free tools',
      allResources: 'All resources',
      builds: 'Builds',
      about: 'About the founder',
      contact: 'Contact',
      newsletter: 'Newsletter',
      changelog: 'Changelog',
      account: 'Account',
      login: 'Log in',
      apiDocs: 'API and agents',
    },
  },
  finalCta: {
    eyebrow: 'Next step',
    title: 'Ready to put AI to work as a real workflow?',
    body: 'Start with the foundations course, keep your progress locally and sync everything to your free account whenever you like.',
    secondary: 'Browse resources',
  },
  newsletter: {
    eyebrow: 'Newsletter',
    title: 'Better AI workflows, once a week.',
    body: 'New lessons, tool updates, API ideas and concrete examples - straight to your inbox.',
    perks: [
      'A new hands-on lesson, guide or tool every week',
      'Fresh workflow templates, prompts and API ideas',
      'No spam. Unsubscribe in one click.',
    ],
    nameLabel: 'Name',
    namePlaceholder: 'What should we call you?',
    emailLabel: 'Email',
    emailPlaceholder: 'you@email.com',
    consentPrefix:
      'Yes, email me the newsletter (at most weekly). I can unsubscribe anytime in one click. Details in the',
    consentLink: 'privacy policy',
    submit: 'Subscribe to the newsletter',
    success: 'Thanks. Your subscription has been saved.',
  },
  forms: {
    genericError: 'That did not work. Please check your entries.',
  },
  consent: {
    banner: {
      regionLabel: 'Cookie settings',
      title: 'Cookies and external media',
      body: 'We use necessary cookies. With your consent we also use analytics and external media such as YouTube videos.',
      onlyNecessary: 'Only necessary',
      customize: 'Customize',
      customizeTitle: 'Customize cookie settings',
      policyLink: 'Cookie policy',
      acceptAll: 'Accept all',
      essential: 'Necessary',
      essentialHint: 'Always on. Required for the site to work.',
      analytics: 'Analytics',
      analyticsHint:
        'PostHog EU (Frankfurt): cookies, local storage and session replay with every input masked. Without consent we only count visits, storing nothing on your device.',
      externalMedia: 'External media',
      marketing: 'Marketing',
      back: 'Back',
      savePreferences: 'Save preferences',
    },
    video: {
      title: 'Load external media',
      body: 'This video is embedded via YouTube. If you load it, data may be transferred to YouTube.',
      load: 'Load video',
      settings: 'Cookie settings',
    },
  },
  search: {
    emptyPrompt: 'Search for Claude Code, Codex, APIs, workflows...',
    dialogTitle: 'Search the campus',
    dialogDescription:
      'Search across lessons, courses, resources, changelog and the community.',
    close: 'Close search',
    advanced: 'Open advanced search',
    groups: {
      lessons: 'Lessons',
      courses: 'Courses',
      resources: 'Resources',
      knowledge: 'Knowledge',
      fundamentals: 'Fundamentals',
      builds: 'Builds',
      changelog: 'Changelog',
      community: 'Community',
      glossary: 'Glossary',
      guides: 'Guides',
      compare: 'Comparisons',
      tools: 'Tools',
    },
  },
  pro: {
    badge: 'Pro',
    badgeLabel: 'Part of Agentic School Pro',
    previewLabel: 'Free preview',
    gateEyebrow: 'Agentic School Pro',
    gateTitle: 'This is where it gets good.',
    gateText:
      'The intro was on me. The rest of this lesson is the real build: the exact setup, the commands, and the mistakes I already paid for so you do not have to. Unlock it with Pro and keep your momentum.',
    gateSignedInText:
      'You are on the free plan right now. Upgrade to Pro and this lesson opens right here, no reload needed.',
    lockedHeading: 'Still waiting for you in this lesson',
    lockedCount: '{count} more sections',
    unlockCta: 'Unlock with Pro',
    priceNote:
      'From {price}/month, billed yearly. 30-day money-back guarantee.',
    startFreeCta: 'Start free with Course 1',
    signInCta: 'Already Pro? Sign in',
    loadingLabel: 'Unlocking your Pro lesson',
    errorText:
      'Your Pro lesson could not be loaded. Check your connection and reload the page.',
    courseTitle: 'This course is part of Pro',
    courseText:
      'Every lesson has a free preview, so you can see exactly what you get. Pro unlocks all 7 full lessons here, plus every other Pro course and every certificate.',
    lockedSectionsLabel: 'Locked sections',
    earlyAccessLabel: 'Early access',
    earlyAccessText:
      'This is a Pro lesson, and it is free for now. Enjoy it, and lock in the launch price before checkout opens.',
    earlyAccessCta: 'Join founding members',
    courseEarlyTitle: 'Pro course, free during early access',
    courseEarlyText:
      'Every lesson in this course is open right now. Join the founding members and you keep the launch price for as long as you stay, plus you hear first when checkout opens.',
  },
  upsell: {
    eyebrow: 'Agentic School Pro',
    bullets: [
      'Courses 3 to 5: auth, data, payments, automation, security',
      'A certificate for every course',
      'Pro templates, skill packs and priority answers',
    ],
    ctaPlans: 'See Pro plans',
    ctaFounding: 'Lock in the launch price',
    ctaPreview: 'Open Course 3',
    earlyNote:
      'Early access: every Pro lesson is open right now. Founding members keep the launch price for as long as they stay.',
    lessonEndEyebrow: 'You finished the free path',
    lessonEndTitle: 'Now build the real thing.',
    lessonEndText:
      'You can set up agents, prompt them well and ship. Course 3 is where that turns into a product people pay for: sign-in, a real database, payments and a clean launch. That is the Pro path, and I would love to walk it with you.',
    dashboardTitle: 'Go all the way with Pro',
    dashboardText:
      'Courses 1 and 2 are yours. Pro adds the three courses that turn a side project into a real product, plus every certificate.',
    courseTitle: 'Where this path leads',
    courseText:
      'After Courses 1 and 2, Pro takes you from shipping a page to running a product: auth, data, payments, automation and security.',
    nextEyebrow: 'Up next',
    nextLessonCta: 'Continue with lesson {number}',
    nextCourseEyebrow: 'Course complete. Nice work.',
    nextCourseCta: 'Start Course {number}',
    finishedEyebrow: 'Path complete',
    finishedTitle: 'You made it through every course.',
    finishedText:
      'That is rare. Grab your certificates on your dashboard, and come back for new lessons as the tools move.',
    finishedCta: 'Open my dashboard',
  },
  toolLead: {
    eyebrow: 'Free, and it stays free',
    title: 'Liked {tool}? Get the next one first.',
    text: 'One email a week with a practical AI workflow tip, plus every new free tool the day it ships. Double opt-in, unsubscribe in one click.',
  },
  pricing: {
    eyebrow: 'Pricing',
    title: 'Start free. Go all the way with Pro.',
    text: 'Courses 1 and 2, every guide, tool and glossary page stay free. Pro is the full path: auth, data, payments, automation, security and shipping your own agentic product. Pick what fits, change it anytime.',
    toggleLabel: 'Billing period',
    yearly: 'Yearly',
    monthly: 'Monthly',
    perMonth: '/month',
    billedYearly: 'billed yearly ({total})',
    billedMonthly: 'billed monthly, cancel anytime',
    freeBilling: 'No card. No time limit.',
    freeNote: 'No card needed. Upgrade whenever you are ready.',
    savePercent: 'Save {percent}%',
    recommended: 'Recommended',
    seatsLabel: 'Seats',
    seatsDecrease: 'Remove a seat',
    seatsIncrease: 'Add a seat',
    seatsIncluded: '{count} seats included',
    extraSeat: 'extra seats {price}/month each',
    extraUsageLabel: 'Extra usage:',
    extraUsageText:
      'need more people on a Team plan? Add seats anytime for {price} per seat per month, billed with your plan.',
    ctaFree: 'Start free',
    ctaGetPro: 'Get Pro',
    ctaGetTeam: 'Get Team',
    ctaWaitlist: 'Join the founding list',
    ctaManage: 'Manage subscription',
    ctaSwitch: 'Switch plan',
    ctaIncluded: 'Included in your team',
    ctaRedirecting: 'Opening secure checkout...',
    earlyAccessNote:
      'Early access: every Pro lesson is free right now. Join the founding list to lock in the launch price.',
    canceledNotice:
      'Checkout canceled, nothing was charged. Your plan is right here whenever you are ready.',
    guarantee: '30-day money-back guarantee',
    guaranteeTitle: '30-day money-back guarantee',
    guaranteeText:
      'Try Pro for a full month. If it is not for you, write me within 30 days and you get every cent back. No forms, no questions, no hard feelings.',
    guaranteeSealRing: 'Money-back guarantee',
    guaranteeSealDays: 'days',
    errors: {
      generic:
        'Checkout could not open right now. Please try again in a moment.',
      rateLimited:
        'That was a lot of clicks. Give it a minute, then try again.',
      notLive:
        'Checkout is not open yet. Join the founding list and you will be first.',
      authRequired: 'Please sign in first, then try again.',
      noCustomer:
        'There is no billing account yet. It appears after your first purchase.',
    },
    founding: {
      eyebrow: 'Founding members',
      title: 'Lock in the launch price before checkout opens.',
      text: 'Pro is free while we are in early access. Join the founding list and you get the launch price locked in for as long as you stay subscribed, plus the first invite when checkout opens. No payment now, and exactly one email when it is time.',
      perks: [
        'The launch price, locked in for as long as you stay',
        'First invite the day checkout opens',
        'Every Pro lesson stays open for you meanwhile',
      ],
      planLabel: 'I am interested in',
      intervalYearly: 'Billed yearly',
      intervalMonthly: 'Billed monthly',
      emailLabel: 'Your email',
      emailPlaceholder: 'you@company.com',
      consent:
        'Email me when founding-member checkout opens. I can unsubscribe anytime. Details in the',
      consentLink: 'privacy policy',
      submit: 'Join the founding list',
      submitting: 'Joining...',
      success:
        'You are on the list. I will email you the moment checkout opens, with your launch price locked in.',
      pending: 'Almost there: check your inbox and confirm your spot. The link is valid for 7 days.',
      updated:
        'You were already on the list, so I just updated your preference.',
      invalidEmail: 'That email does not look right. Mind checking it?',
      consentRequired: 'Please tick the box so I am allowed to email you.',
      rateLimited: 'Too many tries in a short time. Please wait a bit.',
      fineprint: 'No payment now. One email, no spam, unsubscribe in one click.',
    },
    compare: {
      eyebrow: 'Compare plans',
      title: 'Everything side by side.',
      text: 'No asterisks, no hidden limits. Here is exactly what each plan includes.',
      featureHeader: 'What you get',
      included: 'Included',
      notIncluded: 'Not included',
      rows: {
        freeCourses: 'Courses 1 and 2 (14 lessons)',
        proCourses: 'Courses 3 to 5 (21 lessons)',
        guides: 'Guides, tools, glossary and comparisons',
        progress: 'Progress, streaks and stats',
        community: 'Community',
        certificates: 'Certificates',
        templates: 'Pro templates and skill packs',
        priorityQa: 'Priority answers to your questions',
        seats: 'Seats',
        invoice: 'One invoice for the whole team',
        onboarding: 'Personal onboarding call',
        guarantee: '30-day money-back guarantee',
      },
      values: {
        previewOnly: 'Free preview',
        courses12: 'Courses 1-2',
        allCourses: 'Every course',
        oneSeat: '1',
        teamSeats: '5 + extra seats',
        notNeeded: 'Nothing to refund',
      },
    },
    faq: {
      eyebrow: 'Billing FAQ',
      title: 'Questions before you buy.',
      text: 'Short, honest answers. Anything missing? Write me and I will answer personally.',
      items: [
        {
          question: 'Can I cancel anytime?',
          answer:
            'Yes. One click in the billing portal, no calls, no emails. You keep Pro until the end of the period you already paid for, then you are back on Free with all your progress intact.',
        },
        {
          question: 'How does the 30-day money-back guarantee work?',
          answer:
            'If Pro is not for you, write me within 30 days of your first payment and you get a full refund. No forms, no questions.',
        },
        {
          question: 'Monthly or yearly, what is the difference?',
          answer:
            'Same Pro, different rhythm. Yearly is {yearlyPrice} per month, billed once a year ({yearlyTotal}). Monthly is {monthlyPrice} per month, cancel anytime. Yearly saves you about {percent}%.',
        },
        {
          question: 'What happens to my progress if I cancel?',
          answer:
            'Nothing gets deleted. Your progress, streaks and certificates stay. Pro lessons go back to their free preview, and Courses 1 and 2 stay fully yours.',
        },
        {
          question: 'How do Team seats work?',
          answer:
            'Team includes {teamSeats} seats: yours plus your teammates. You assign seats by email in your billing settings, and a teammate gets Pro the moment they sign in with that verified email. Need more people? Extra seats are {extraSeat} per seat per month.',
        },
        {
          question: 'Do I get an invoice? Is paying secure?',
          answer:
            'Yes. Every payment comes with an invoice you can download in the billing portal. Payments run through Stripe, so your card details never touch our servers.',
        },
        {
          question: 'What about the 14-day right of withdrawal?',
          answer:
            'Pro starts the moment you pay, and at checkout you agree to that immediate start, which ends the statutory 14-day withdrawal right for digital content in the EU and UK. Our voluntary 30-day money-back guarantee still covers you.',
        },
      ],
    },
    plans: {
      free: {
        name: 'Free',
        tagline: 'Everything you need to start.',
      },
      pro: {
        name: 'Pro',
        tagline: 'The whole academy, for one builder.',
      },
      team: {
        name: 'Team',
        tagline: 'Pro for your whole team, one invoice.',
      },
    },
    features: {
      freeCourses: 'Courses 1 and 2 in full',
      allGuides: 'All guides, tools, glossary and comparisons',
      progress: 'Progress tracking, streaks and stats',
      community: 'Community access',
      everythingInFree: 'Everything in Free',
      proCourses: 'Courses 3 to 5: auth, data, payments, automation, security',
      allCertificates: 'Certificates for every course',
      proTemplates: 'Pro templates and skill packs',
      priorityQa: 'Priority answers to your questions',
      everythingInPro: 'Everything in Pro',
      teamSeats: '5 seats included, add more anytime',
      oneInvoice: 'One invoice for the whole team',
      onboardingCall: 'Personal onboarding call',
    },
  },
  billing: {
    title: 'Billing',
    text: 'Your plan, renewal and invoices in one place.',
    signedOut: 'Sign in to see your plan and manage billing.',
    signIn: 'Sign in',
    loading: 'Loading your plan',
    currentPlan: 'Current plan',
    billingLabel: 'Billing',
    intervalYear: 'Yearly',
    intervalMonth: 'Monthly',
    seatsLabel: 'Seats',
    seatsValue: '{count} seats',
    renewsLabel: 'Renews on',
    endsLabel: 'Access ends on',
    cancelNotice:
      'Your subscription is canceled and ends on {date}. You keep full Pro access until then. Changed your mind? Resume it in the billing portal.',
    pastDueNotice:
      'Your last payment did not go through. Update your payment method in the billing portal to keep Pro.',
    sourceSeat:
      'You have Pro through a seat on your team. Your team owner manages billing.',
    sourceGrant: 'You have complimentary Pro access until {date}.',
    sourceAdmin: 'Admin account: you always have full Pro access.',
    freeText:
      'You are on Free: Courses 1 and 2, every guide and every tool. Pro unlocks Courses 3 to 5 and every certificate.',
    manage: 'Manage billing',
    opening: 'Opening...',
    manageHint:
      'Cancel, switch plans, change seats, update your card and download invoices in the secure Stripe portal.',
    seePlans: 'See plans',
    earlyTitle: 'Early access is on',
    earlyText:
      'Every Pro lesson is free right now. Join the founding list to lock in the launch price before checkout opens.',
    activatingTitle: 'Activating your Pro access...',
    activatingText:
      'Payment received, thank you! Your access unlocks in a few seconds, no need to refresh.',
    slowText:
      'This is taking longer than usual. Your payment is safe. Refresh in a minute, and if Pro still does not show up, write me and I will fix it right away.',
    activatedTitle: 'Welcome to Pro!',
    activatedText: 'Everything is unlocked. Time to build something real.',
    activatedCta: 'Start Course 3',
    status: {
      active: 'Active',
      trialing: 'Trial',
      pastDue: 'Payment issue',
      canceled: 'Canceled',
      unpaid: 'Unpaid',
      incomplete: 'Incomplete',
      paused: 'Paused',
    },
    team: {
      title: 'Your team',
      text: 'Give your teammates their seats. They get Pro as soon as they sign in with the email you add here (it has to be verified).',
      seatsUsed: '{used} of {total} seats assigned (plus yours)',
      emailLabel: 'Teammate email',
      emailPlaceholder: 'name@company.com',
      assign: 'Add seat',
      assigning: 'Adding...',
      remove: 'Remove',
      removeLabel: 'Remove the seat of {email}',
      inactive: 'Inactive: over your seat limit',
      empty: 'No seats assigned yet. Add your first teammate below.',
      needMore:
        'Need more seats? Change the quantity in the billing portal and they show up here instantly.',
      errors: {
        invalidEmail: 'That email does not look right.',
        ownEmail: 'That is your own email, and you already have a seat.',
        alreadyAssigned: 'That teammate already has a seat.',
        seatsFull:
          'All seats are taken. Add more seats in the billing portal first.',
        rateLimited: 'Too many changes in a short time. Please wait a bit.',
        generic: 'That did not work. Please try again.',
      },
    },
  },
  // Double opt-in, confirmation and preference-center copy (M3).
  email: {
    inbox: {
      title: 'Check your inbox',
      body: 'I just sent a confirmation link to {email}. One click and you are in. Nothing there? Check your spam or promotions folder. The link is valid for 7 days.',
      again: 'Use a different email',
    },
    confirm: {
      eyebrow: 'Email confirmation',
      loadingTitle: 'Confirming...',
      loadingText: 'One second, I am checking your link.',
      newsletterTitle: 'You are in. Welcome!',
      newsletterText: 'Your newsletter subscription is confirmed. Your welcome email with the best free resources is on its way. Want to start right now?',
      newsletterCta: 'Start Course 1',
      newsletterSecondary: 'Explore the free tools',
      foundingTitle: 'Your founding spot is confirmed',
      foundingText: 'Your launch price is locked in. Every Pro lesson is open to you during early access, and I will email you exactly once when checkout opens.',
      foundingCta: 'See what Pro includes',
      foundingSecondary: 'Open a Pro course',
      alreadyTitle: 'Already confirmed',
      alreadyText: 'This link was used before and you are all set. There is nothing else to do.',
      expiredTitle: 'This link has expired',
      expiredText: 'Confirmation links are valid for 7 days. Sign up again and I will send you a fresh one.',
      invalidTitle: 'This link does not work',
      invalidText: 'The link is incomplete or was replaced by a newer one. Sign up again and you will get a fresh link.',
      retryCta: 'Sign up again',
      homeCta: 'Back to the homepage',
    },
    unsubscribe: {
      eyebrow: 'Email preferences',
      title: 'Manage your emails',
      intro: 'Emails to {email}. Changes apply immediately.',
      unsubscribeTitle: 'Unsubscribe in one click',
      unsubscribeText: 'Stop the emails this link came from. Service emails about an active subscription still reach you.',
      unsubscribeButton: 'Unsubscribe',
      unsubscribeAll: 'Unsubscribe from everything',
      doneTitle: 'You are unsubscribed',
      doneText: 'You will not get these emails anymore. Changed your mind? Switch them back on below.',
      listsTitle: 'Your lists',
      lists: {
        newsletter: {
          label: 'Newsletter',
          hint: 'One practical email a week at most: lessons, tools, workflows.',
        },
        founding: {
          label: 'Founding-member updates',
          hint: 'One email when checkout opens, with your launch price.',
        },
        product: {
          label: 'Account and product updates',
          hint: 'Occasional news about your account and new courses.',
        },
      },
      statusOn: 'Subscribed',
      statusOff: 'Not subscribed',
      statusPending: 'Waiting for confirmation',
      statusBounced: 'Paused (delivery failed)',
      save: 'Save preferences',
      saving: 'Saving...',
      saved: 'Saved. Your preferences are up to date.',
      loading: 'Loading your preferences...',
      invalidTitle: 'This link does not work',
      invalidText: 'The link is incomplete or invalid. Use the link from your latest email, or manage everything in your account settings.',
      settingsCta: 'Account settings',
      noLists: 'There are no email lists for this address.',
      error: 'That did not work. Please try again.',
    },
    settings: {
      newsletterTitle: 'Newsletter',
      newsletterLabel: 'Weekly newsletter (lessons, tools and workflows)',
      newsletterHint: 'Goes to your verified account email. Unsubscribe anytime in one click.',
      newsletterOn: 'You are subscribed.',
      newsletterOff: 'You are not subscribed.',
      subscribe: 'Subscribe to the newsletter',
      unsubscribe: 'Unsubscribe from the newsletter',
      saving: 'Saving...',
      unavailable: 'Please verify your account email first.',
    },
  },
  notFound: {
    label: 'Page not found',
    body: 'The content you requested does not exist or has not been published yet.',
    home: 'Back to home',
  },
  routeError: {
    title: 'Something went wrong',
    body: 'This page could not be loaded. Please try again.',
    retry: 'Try again',
    home: 'Back to home',
  },
  pages: {
    common: {
      start: 'Start lesson',
      open: 'Open',
      available: 'Available',
      roadmap: 'Roadmap',
      lesson: 'Lesson',
      resource: 'Resource',
      fundamental: 'Fundamental',
      fundamentals: 'Fundamentals',
      lessonsWord: 'lessons',
      minutesShort: 'min',
      readingMinutes: 'min read',
      starterPathOpen: 'Open the starter path',
      viewResources: 'Browse resources',
      toCourses: 'Go to courses',
      allCourses: 'All courses',
      filter: 'Filter',
      saving: 'Saving...',
    },
    breadcrumbs: {
      start: 'Home',
      courses: 'Courses',
      knowledge: 'Knowledge',
      resources: 'Resources',
      community: 'Community',
      changelog: 'Changelog',
      glossary: 'Glossary',
      compare: 'Comparisons',
      guides: 'Guides',
      tools: 'Tools',
    },
    notFound: {
      lesson: 'Lesson not found',
      course: 'Course not found',
      topic: 'Topic not found',
      post: 'Post not found',
      resource: 'Resource not found',
      article: 'Article not found',
      changelogEntry: 'Changelog entry not found',
    },
    home: {
      heroEyebrow: 'Agentic engineering, from beginner to expert.',
      heroTitleA: 'Learn AI workflows',
      heroTitleB: 'that actually',
      heroTitleUnderline: 'work',
      heroLead:
        'A public-first campus for agentic engineering, Claude Code, Codex, Pi, APIs and AI workflows - built for measurable business ROI, not tool hype.',
      heroMetricsLabel: 'Campus metrics',
      metricLessons: 'lessons',
      metricResources: 'resources',
      metricFreeLessons: 'free to start, no card',
      stagePreviewLabel: 'Campus OS preview',
      stageOs: 'Campus OS',
      stageCockpit: 'Agentic engineering cockpit',
      stagePublicFirst: 'Public-first',
      stageLivePath: 'Live learning path',
      stagePathTitle: 'From setup to a production workflow',
      stageLessons: '35 lessons',
      // Accessible/crawlable name for the featured-lesson "Start lesson" link.
      // Contains the visible label ("Start lesson") so it satisfies WCAG "label
      // in name", and is descriptive enough to pass Lighthouse SEO link-text
      // (which evaluates the visible text, not just the accessible name).
      startLessonCta: 'Start lesson: {title}',
      stageRoiLabel: 'ROI radar',
      stageRoiText: 'Playbooks',
      stageToolLabel: 'Tool stack',
      stageToolText: 'Categories',
      stageLaunchLabel: 'Launch',
      stageLaunchText: 'live',
      solutionLabel: 'The solution',
      proofWhyLabel: 'Why this campus',
      proofLessons: '35 lessons',
      proofLessonsText: 'from your first setup to the capstone',
      proofResources: '25 resources',
      proofResourcesText: 'templates, checklists, prompts, APIs',
      proofGerman: 'Public-first',
      proofGermanText: 'read, save and learn without signing up',
      systemEyebrow: 'An operating system, not a course archive',
      systemTitle: 'A campus that runs like a production system.',
      systemLead:
        'Every lesson leads to a clear artefact: setup, workflow, review, checklist or ROI decision.',
      valueToolClarityTitle: 'Tool clarity',
      valueToolClarityText:
        'Claude Code, Codex, Pi and APIs are taught as one connected system for real tasks, not as isolated tools.',
      valueWorkflowTitle: 'Workflow design',
      valueWorkflowText:
        'You translate processes into specifications, context, tests, reviews and repeatable routines.',
      valueRoiTitle: 'Business ROI',
      valueRoiText:
        'Every decision is weighed against time saved, quality, risk and operational impact.',
    },
    problem: {
      label: 'The problem',
      title: 'You want to be free. Not stuck in the 9-to-5.',
      lead: 'You feel it every morning: your time belongs to someone else. You want your own path, real independence, and you sense that AI is the way out. You just have no one to tell you how to get there.',
      card1Title: 'Stuck in the 9-to-5',
      card1Text:
        'Every day you trade your life for a salary other people set. You want out, but the exit is impossible to see from the inside.',
      card2Title: 'AI is your chance - but how?',
      card2Text:
        'Deep down you know AI is the lever that changes everything. You just do not know how to put it to work for you when you have never written code.',
      card3Title: 'The terminal feels intimidating',
      card3Text:
        'Claude Code in a black terminal window looks like another world. No software background, no experience, and yet you are supposed to type commands.',
      card4Title: 'No one shows you the way',
      card4Text:
        'No course, no school, no YouTube channel meets you exactly where you are. What is missing is someone who really talks to YOU and understands what starting from zero feels like.',
      bridge:
        'Imagine someone who walked this exact path themselves. From zero coding experience and no AI knowledge to the point where you truly master it and can finally build what frees you from the trap. That is exactly what this campus is for.',
      bridgeStrong1: 'zero coding experience',
      bridgeStrong2: 'no AI knowledge',
      cta: 'Show me the way',
    },
    founder: {
      label: 'Who am I?',
      imageAlt: 'Nico Jaroszewski, founder and tutor of Agentic School',
      nameCard: 'Nico Jaroszewski · Founder & tutor',
      heading: 'The fastest way from A to B.',
      p1: 'Hi, I am Nico. I have been in this since agentic coding became a thing - and I have built and tried just about everything myself: Claude Code, Codex, Pi, APIs and real automations in daily use.',
      p2: 'I build, write and teach this campus myself. Every lesson comes from real practice instead of theory - including the detours, mistakes and aha moments I went through, so you do not have to live them all yourself.',
      p3Pre: 'That is exactly what the ',
      p3Strong1: 'AB',
      p3Mid: ' in Agentic Business Academy stands for: it is your ',
      p3Strong2: 'A-to-B Academy',
      p3Post:
        '. Instead of grinding through months of trial and error, you get a clear structure that gets you to your goal fast. I hand you every trick and shortcut I learned along the way.',
      tag1: 'Here since day one',
      tag2: 'Agentic engineer',
      tag3: 'From Switzerland',
    },
    darkBand: {
      pathLabel: 'My path',
      addLesson: '+ Lesson',
      today: 'Today',
      todayTitle: 'Set up Codex',
      todayMeta: '16 min · Setup',
      thisWeek: 'This week',
      thisWeekTitle: 'Write a specification',
      thisWeekMeta: '22 min · Workflow',
      capstone: 'Capstone',
      capstoneTitle: '7-day rollout',
      capstoneMeta: 'Business ROI',
      eyebrow: 'How the campus works',
      title: 'Learn like a product team, not like a course attendee.',
      lead: 'Setup, specification, review and rollout - all in one clear path, with the tools you use in your business every day.',
      feature1Title: 'A clear learning path',
      feature1Text:
        'Five courses, 35 lessons, each with an outcome and a concrete artefact.',
      feature2Title: 'Real workflows',
      feature2Text:
        'Claude Code, Codex and Pi are used as one integrated system.',
      feature3Title: 'Business ROI radar',
      feature3Text:
        'Every decision is weighed against time saved, quality and risk.',
      feature4Title: 'Public-first',
      feature4Text:
        'Read, save and learn locally - sign up only to sync your progress.',
    },
    splitCta: {
      roiOverview: 'ROI overview',
      label: 'Concrete business ROI',
      eyebrow: 'ROI radar',
      titleA: 'See your time savings',
      titleB: 'before you start.',
      lead: 'Every workflow on the campus is scored against time saved, quality and risk. You decide based on numbers, not tool hype.',
      cta: 'Calculate ROI',
      cardTitle: 'Time saved per week',
      cardSetup: 'Setup',
      row1: 'Codex workflow',
      row2: 'Claude Code review',
      row3: 'Pi automation',
      row4: 'Capstone rollout',
      row4Value: '34h saved',
      cardCta: 'View workflow',
      // Labels inside the ROI mock widget in the hero. Product names (Codex,
      // Spec) stay as brand terms across locales.
      roadmapWorkflow: 'Roadmap workflow',
      codexSetup: 'Codex setup',
      specReview: 'Spec review',
      agenticApi: 'Agentic API',
      capstoneRun: 'Capstone run',
      capstoneTitle: '7-day rollout',
    },
    courseRail: {
      eyebrow: 'Curriculum',
      title: 'A clear path from setup to capstone.',
      lead: 'The academy takes you from tool fluency to robust workflows, APIs, governance and a production-ready 7-day rollout.',
      cta: 'View all courses',
    },
    roadmap: {
      eyebrow: 'Roadmap',
      title: 'All 35 lessons in a sensible order.',
      lead: 'Pick a lesson and start right away - no sign-up required, progress is saved locally.',
      courseOf: 'Course {current} of {total}',
      startNow: 'Start now',
      prevLessons: 'Previous lessons',
      nextLessons: 'Next lessons',
      coursesLabel: 'Courses',
      courseDotLabel: 'Course {index}: {title}',
    },
    testimonials: {
      label: 'Voices from practice',
      title: 'Do not learn in a vacuum.',
      lead: 'Founders and teams use the academy to turn agentic workflows into real business processes.',
      cards: [
        {
          role: 'Operations Lead, SaaS scale-up',
          title: 'No more tool chaos',
          quote:
            'Before, we had five AI tools and no common thread. Today our onboarding runs on one clear Claude Code workflow the whole team understands. It was the first AI rollout that actually stuck.',
        },
        {
          role: 'Managing Director, mid-market',
          title: 'We decide with numbers, not hype',
          quote:
            'The ROI logic from the campus ended 80 percent of our tool debates. We score every workflow by time saved and risk before we invest. It noticeably shortened our meetings.',
        },
        {
          role: 'Head of Product, B2B platform',
          title: 'Two weeks, more impact than six months',
          quote:
            'We automated more in two weeks than in the six months before. The difference was not the tool, but finally understanding which workflow pulls which lever.',
        },
        {
          role: 'Freelance developer',
          title: 'From skeptic to daily user',
          quote:
            'I was convinced coding agents were just a gimmick. After the starter path, Codex is part of my everyday work: reading issues, writing tests, preparing reviews. I ship faster and calmer.',
        },
        {
          role: 'Owner, marketing agency',
          title: 'A content system instead of a one-person show',
          quote:
            'My expertise turned into a repeatable content system my team can run without me. The lessons are concrete enough to put into practice the same day.',
        },
        {
          role: 'Solo founder',
          title: 'Public-first won me over',
          quote:
            'I could read and try everything before I even needed an account. The capstone helped me take my first real workflow live within a week.',
        },
        {
          role: 'Data Lead, logistics',
          title: 'Finally clean data extraction',
          quote:
            'PDFs, spreadsheets and emails were always manual work for us. With the workflow from the API course we now pull the data structured and verified. It saves my team hours every week.',
        },
        {
          role: 'CTO, fintech startup',
          title: 'Governance we actually use',
          quote:
            'Human-in-the-loop and evals sounded like theory to me. The governance course turned them into a checklist that runs in our daily work. We are faster and still auditable.',
        },
        {
          role: 'Consultant, SME digitalisation',
          title: 'Exactly my clients language',
          quote:
            'Finally material I can hand straight to clients without translating it. The examples hit exactly the processes that really hurt in mid-market companies.',
        },
        {
          role: 'Head of Sales',
          title: 'Lead research that pays off',
          quote:
            'Our lead research now takes a fraction of the time and delivers better hits. I adopted the workflow one to one from the business ROI course and immediately saw how much sticks.',
        },
        {
          role: 'Project lead, industry',
          title: 'From meeting straight to task',
          quote:
            'Conversation notes now turn into structured tasks with owners. Sounds small, but it changes the whole rhythm of the week. I lose nothing anymore.',
        },
        {
          role: 'Owner, web studio',
          title: 'Convex, Clerk and Vercel finally understood',
          quote:
            'I struggled with the modern stack for a long time. The lessons explain Convex, Clerk and Vercel as one connected system, not as isolated tools. My last project was the first without tool frustration.',
        },
      ],
    },
    pricing: {
      label: 'Pricing',
      eyebrow: 'Pricing',
      title: 'Start free. Upgrade when it clicks.',
      lead: 'Courses 1 and 2 are yours for free, no card needed. When you are ready for auth, payments, automation and shipping your own product, Pro has your back.',
      compareLink: 'Compare all plans and the billing FAQ',
    },
    howItWorks: {
      label: 'How it works',
      eyebrow: 'How it works',
      title: 'From zero to your own AI workflows in four steps.',
      lead: 'A clear path instead of tool chaos: sign up, learn, build and make the measurable value for your business visible.',
      step1Title: 'Sign up for free',
      step1Text:
        'Create your free account in a minute. No credit card. Courses 1 and 2, every guide and every tool are yours right away.',
      step2Title: 'Start the learning path',
      step2Text:
        'Begin with the setup and build your first workflows. Every lesson delivers a concrete artefact you use immediately.',
      step3Title: 'Build your own AI workflows',
      step3Text:
        'Translate real tasks into repeatable workflows with Claude Code, Codex, Pi and APIs that run in your everyday work.',
      step4Title: 'Measure and scale business ROI',
      step4Text:
        'Score every workflow by time saved, quality and risk and scale what demonstrably moves the needle for you.',
    },
    homeFaq: {
      label: 'FAQ',
      eyebrow: 'FAQ',
      title: 'Frequently asked questions about the campus.',
      lead: 'Everything you need to know before you start for free. Still have a question? Just write to us.',
      items: [
        {
          question: 'Is Agentic School really free?',
          answer:
            'A big part of it, yes, and for good. Courses 1 and 2 (14 lessons), every guide, tool and glossary page and the community are free, no credit card needed. Courses 3 to 5 are Agentic School Pro. We are in early access right now, so even the Pro lessons are open to everyone while it lasts.',
        },
        {
          question: 'What is Pro, and what does it cost?',
          answer:
            'Pro is the full path: Courses 3 to 5 (auth, data, payments, automation, security and shipping your own agentic product), every certificate, Pro templates and priority answers. It is {yearlyPrice} per month billed yearly, or {monthlyPrice} month to month. Join the founding members now and you lock in the launch price for as long as you stay.',
        },
        {
          question: 'Can I cancel anytime?',
          answer:
            'Yes. One click in the billing portal, no calls, no emails. You keep Pro until the end of the period you paid for, and your progress, streaks and certificates stay yours.',
        },
        {
          question: 'Is there a money-back guarantee?',
          answer:
            'Yes, 30 days. If Pro is not for you, write me within 30 days of your first payment and you get every cent back. No forms, no questions.',
        },
        {
          question: 'Do I need programming experience?',
          answer:
            'No. The campus is built exactly for people starting from zero. You begin with the setup and work your way step by step toward real workflows. Every lesson explains the why, not just the how, so you keep up even without a coding background.',
        },
        {
          question: 'Which tools do I learn (Claude Code, Codex, Pi, APIs)?',
          answer:
            'You learn Claude Code, Codex and Pi as one connected system instead of isolated tools. On top of that comes the practical use of APIs, so you extract data, automate processes and build your own AI workflows for your business.',
        },
        {
          question: 'Who is the campus for?',
          answer:
            'For founders, solo entrepreneurs, teams and anyone who wants to use AI for measurable business ROI instead of tool hype. Whether you are brand new or already have some experience, the learning path meets you where you are.',
        },
        {
          question: 'Do I have to sign up to see content?',
          answer:
            'No. The campus is public-first. You can read the free lessons, guides and resources and build local progress without an account. Signing up only pays off once you want to sync your progress and join the community.',
        },
        {
          question: 'How up to date is the content?',
          answer:
            'The content comes from daily practice and is updated continuously as tools or workflows change. New lessons and resources land regularly: the free ones for everyone, and every new Pro lesson is included in Pro at no extra charge.',
        },
        {
          question: 'What language is the content in?',
          answer:
            'The content is available in clear language with examples from everyday business. You can adopt templates and workflows directly without translating them first.',
        },
      ],
    },
    courses: {
      eyebrow: 'Courses',
      title: 'Agentic engineering, structured for business ROI',
      text: 'Browse learning paths, tools and lessons. The campus is public-first: you can read, save and build local progress before you sign up.',
      programEyebrow: 'Lesson programme',
      programTitle: 'All 35 lessons, sorted by course.',
      programLead:
        'Every lesson delivers a clear outcome, artefacts and concrete tools. Click through freely, progress is saved locally.',
      openCourse: 'Open course',
      filters: [
        'Getting started',
        'Business',
        'Technology',
        'API',
        'Automations',
      ],
    },
    course: {
      eyebrowPrefix: 'Course',
      lessonsLabel: 'Lessons',
      estimatedLabel: 'Estimated learning time',
      availableLabel: 'Already available',
      startFirstLesson: 'Start the first lesson',
      outcomeTitle: 'What you can do after this course',
      outcome1: 'Place agentic engineering tools cleanly in context',
      outcome2: 'Plan workflows with clear quality assurance',
      outcome3: 'Make business ROI and operational risks visible',
      progressLabel: 'Course progress',
      progressText: 'lessons published. The rest follows the roadmap.',
      timeLabel: 'Learning time',
      timeText: 'Including reading, examples and a small hands-on artefact.',
    },
    lesson: {
      lessonPrefix: 'Lesson',
      whatYouLearn: 'What you learn',
      inThisLesson: 'In this lesson',
      transcript: 'Transcript',
      save: 'Save',
      like: 'Like',
      complete: 'Complete',
      done: 'Done',
      comments: 'Comments',
      next: 'Next',
      actionsLabel: 'Lesson actions',
      fundamentalsHeading: 'Fundamentals',
      fundamentalsTag: 'Fundamental',
    },
    comments: {
      heading: 'Comments',
      loading: 'Loading comments.',
      member: 'Community member',
      report: 'Report',
      reportReason: 'Reported from the comment surface',
      reportThanks: 'Thanks. The report is now in the moderation queue.',
      reportNeedsAccount: 'Reporting is only possible with a verified account.',
      commentLabel: 'Comment',
      placeholder: 'Write a concrete question or addition.',
      submit: 'Post a comment',
      published: 'Your comment has been published.',
      emptyPrefix: 'No comments on ',
      emptySuffix: ' yet. Sign up for free to ask the first question.',
      fallbackPrefix: 'Comments on ',
      fallbackSuffix:
        ' are being prepared. The lesson stays fully readable, and with a free account you can ask questions later.',
    },
    community: {
      eyebrow: 'Community',
      title: 'Do not learn alone',
      text: 'Ask questions, share workflows, get feedback and see how others apply agentic engineering in real businesses.',
      allTopics: 'All topics',
      workflowReviews: 'Workflow reviews',
      topicsEyebrow: 'Topics',
      topicsTitle: 'All community areas',
      topicsText: 'Find questions, reviews, showcases and tool updates.',
      topicEyebrow: 'Topic',
      postEyebrow: 'Community post',
      postLoadingTitle: 'Loading post',
      postLoadingText: 'The community post is loading from Convex.',
      composerTitle: 'Create a post',
      composerTitleLabel: 'Title',
      composerTitlePlaceholder: 'A concrete question or workflow title',
      composerBodyLabel: 'Post',
      composerBodyPlaceholder:
        'Describe the context, your goal and what you have already tried.',
      composerTagsLabel: 'Tags',
      composerTagsPlaceholder: 'Separate tags with commas',
      composerSubmit: 'Publish post',
      composerPublished: 'Your post has been published.',
      composerLocked:
        'With a free account you can publish questions, showcases and workflow reviews.',
    },
    resources: {
      eyebrow: 'Resources',
      title: 'Tools, APIs, prompts, skills, templates and checklists',
      text: 'A curated library for agentic engineering workflows, focused on setup, privacy, ROI and reusability.',
      viewChangelog: 'View changelog',
      inThisResource: 'In this resource',
      sectionSummary: 'In short',
      sectionGoodFor: 'What it is good for',
      sectionWhen: 'When to use it',
      sectionSetup: 'Setup',
      sectionRisks: 'Risks and privacy',
      typeLabels: {
        tool: 'Tool',
        api: 'API',
        prompt: 'Prompt',
        skill: 'Skill',
        template: 'Template',
        checklist: 'Checklist',
        guide: 'Guide',
      },
    },
    knowledge: {
      eyebrow: 'Knowledge',
      title: 'Agentic engineering guides for your business',
      text: 'In-depth cornerstone articles on definitions, tools, ROI and workflows. Grounded, practical and with direct links to matching lessons and resources.',
      summary: 'In short',
      inThisArticle: 'In this article',
      faqHeading: 'Frequently asked questions',
      relatedHeading: 'Matching lessons & resources',
      categoryLabels: {
        Grundlagen: 'Fundamentals',
        Tools: 'Tools',
        Business: 'Business',
        Technik: 'Technology',
      },
    },
    changelog: {
      eyebrow: 'Changelog',
      title: 'Turning tool updates into practical workflows',
      text: 'Relevant changes to AI agents, APIs and platforms are translated into clear decisions for your business.',
      subscribe: 'Subscribe to updates',
      filterLabel: 'Filter by tool',
      filterAll: 'All tools',
      groupToday: 'Today',
      groupYesterday: 'Yesterday',
      groupThisWeek: 'This week',
      groupEarlier: 'Earlier',
      empty: 'No entries for this tool.',
      entrySections: [
        "What's new?",
        'Why it matters',
        'What you should do now',
        'Impact on workflows',
        'Sources',
      ],
    },
    search: {
      eyebrow: 'Search',
      title: 'Find workflows, tools and lessons',
      text: 'Search across courses, resources, changelog and the community.',
      inputLabel: 'Search',
    },
    newsletter: {
      eyebrow: 'Newsletter',
      title: 'Stay a step ahead of your AI workflows',
      text: 'A new hands-on lesson or guide every week, tool updates, API ideas and concrete examples - curated for measurable business ROI. Sign up right below.',
    },
    contact: {
      eyebrow: 'Contact',
      title: 'Talk to us about your AI workflow',
      text: 'Want to know which agentic engineering approach makes sense for your business? Write a few lines about where you are and what you want to achieve.',
      nameLabel: 'Name',
      emailLabel: 'Email',
      companyLabel: 'Company',
      companyPlaceholder: 'Company optional',
      topicLabel: 'Topic',
      messageLabel: 'Message',
      consentPrefix: 'I accept the',
      consentLink: 'privacy notice',
      submit: 'Get in touch',
      sending: 'Sending...',
      success: 'Thanks. Your message has been saved.',
      topics: [
        'Question about the campus',
        'Partnership',
        'Workflow review',
        'Consulting',
        'Technical issue',
        'Privacy',
        'Other',
      ],
    },
    settings: {
      eyebrow: 'Settings',
      title: 'Manage account and privacy',
      text: 'Adjust your profile, privacy, notifications and your local consent choices.',
      navProfile: 'Profile',
      navPrivacy: 'Privacy',
      navNotifications: 'Notifications',
      navBilling: 'Billing',
      titleOverview: 'Overview',
      titlePrivacy: 'Privacy',
      titleNotifications: 'Notifications',
      signedOut:
        'Sign in to manage your profile, privacy, data export and notifications in Convex.',
      signIn: 'Sign in',
      dataExportTitle: 'Data export',
      dataExportText:
        'Exports profile, saves, likes, progress and comments from Convex.',
      dataExportButton: 'Prepare data export',
      dataExportSummary: 'Export summary',
      dataExportLoading: 'Export is loading.',
      deleteTitle: 'Account deletion',
      deleteText:
        'Deletion anonymises account and profile data and writes an audit log.',
      deletePhrase: 'DELETE ACCOUNT',
      deleteHint: 'To confirm, type {phrase} in the field below.',
      deleteConfirmLabel: 'Confirmation',
      deleting: 'Deleting...',
      deleteButton: 'Delete account',
      deleteSuccess: 'Your account deletion has been scheduled.',
      notifProduct: 'Product updates',
      notifCommunity: 'Community replies',
      notifProgress: 'Progress reminders',
      notifChangelogDigest: 'Daily tool news (changelog digest)',
      notifPublicProfile: 'Show public profile',
      notifSaving: 'Saving...',
      notifSave: 'Save notifications',
      notifSuccess: 'Your notifications have been saved.',
      profileNameLabel: 'Name',
      profileUsernameLabel: 'Username',
      profileCompanyLabel: 'Company',
      profileWebsiteLabel: 'Website',
      profileBioLabel: 'Bio',
      profileBioPlaceholder: 'Short profile',
      profilePublicToggle: 'Enable public profile',
      profileSaving: 'Saving...',
      profileSave: 'Save profile',
      profileManageAvatar: 'Manage avatar and login',
      profileSuccess: 'Your profile has been saved.',
    },
    utility: {
      savedEyebrow: 'Saved',
      savedTitle: 'Your saved content',
      savedText:
        'Anonymous saves stay local and sync to Convex after you log in.',
      progressEyebrow: 'Progress',
      progressTitle: 'Your learning progress',
      progressText:
        'Lessons, video progress and completions are stored locally and migrated after sign-up.',
      onboardingEyebrow: 'Onboarding',
      onboardingTitle: 'Welcome to the campus',
      onboardingChecking: 'Checking local progress.',
      onboardingSignIn: 'Sign in so your local progress can be synced.',
      onboardingNone:
        'No local progress found. You can keep learning right away.',
      onboardingDone: 'Your local progress has been linked to your account.',
      onboardingFailed:
        'The migration could not be completed. Your local progress is preserved.',
    },
    dashboard: {
      eyebrow: 'Your campus',
      title: 'Learning progress and streak',
      text: 'Your streak, your stats and where to pick up next. While anonymous everything stays on this device and syncs after you sign in.',
      streakLabel: 'Day streak',
      streakDays: 'days',
      streakDay: 'day',
      streakActiveToday: 'Active today',
      streakInactiveToday: 'No learning yet today',
      minutesLabel: 'Minutes learned',
      lessonsLabel: 'Lessons completed',
      longestLabel: 'Longest streak',
      continueTitle: 'Continue where you left off',
      continueResume: 'Resume lesson',
      continueNext: 'Start next lesson',
      continueEmpty: 'Start your first lesson to pick up here.',
      continueCta: 'Browse courses',
      newSinceTitle: 'New since your last visit',
      newSinceReplies: 'new repl(ies) to your posts',
      newSinceChangelog: 'new tool updates in the changelog',
      newSinceEmpty: 'Nothing new since your last visit. All caught up.',
      newSinceChangelogCta: 'View changelog',
      coursesTitle: 'Course progress',
      certificateReady: 'Certificate ready',
      loading: 'Loading your progress.',
    },
    certificate: {
      eyebrow: 'Certificate of completion',
      issuedTo: 'Issued to',
      forCourse: 'for completing',
      issuedOn: 'Issued on',
      idLabel: 'Certificate ID',
      verified: 'Verified by Agentic School',
      viewCourse: 'View course',
      backToProgress: 'Go to your progress',
      notFoundTitle: 'Certificate not found',
      notFoundText:
        'This certificate does not exist or has been revoked. Check the link and try again.',
      loading: 'Loading certificate.',
      ctaGet: 'Get your certificate',
      ctaGetting: 'Issuing certificate...',
      ctaView: 'View certificate',
      ctaSignIn: 'Sign in to claim',
      ctaError:
        'The certificate could not be issued. Complete all lessons first.',
    },
    legal: {
      eyebrow: 'Legal',
      updatedPrefix: 'Last updated:',
      cookieButton: 'Open cookie settings',
    },
    auth: {
      loginEyebrow: 'Log in',
      loginTitle: 'Welcome back.',
      loginText:
        'Log in and sync your progress, saved lessons and comments across all your devices.',
      loginSwitchText: 'No account yet?',
      registerEyebrow: 'Start for free',
      registerTitle: 'Create your free account.',
      registerText:
        'Sync your progress, save workflows and ask questions in the community. No credit card, and Courses 1 and 2 stay free.',
      registerSwitchText: 'Already in?',
      registerSwitchCta: 'Go to login',
      profileEyebrow: 'Account',
      profileTitle: 'Profile and settings',
      profileText: 'Manage your email, security and connected accounts.',
      or: 'OR',
      trustLabel: 'Benefits',
      foreverFree: 'Free to start.',
      loginFormHeading: 'Sign in',
      loginFormSubheading: 'Continue with email or your linked account.',
      registerFormHeading: 'Create your account',
      registerFormSubheading:
        'Ready in under a minute - no credit card needed.',
      loginProof: [
        'Progress and saved lessons synced across every device.',
        'Ask questions and learn alongside the community.',
        'Courses 1 and 2 free, no credit card needed.',
      ],
      registerProof: [
        'Build real agentic workflows, not just theory.',
        'Keep builds, prompts and progress in one place.',
        'Courses 1 and 2 free, no credit card needed.',
      ],
      backToSite: 'Back to site',
      swissNote: 'Built in Switzerland. revFADP + GDPR by default.',
      brandName: 'Agentic School',
      offlineProfileHeading: 'Account management is offline',
      offlineAuthHeading: 'Authentication is disabled in preview mode',
      offlineProfileBody:
        'Once Clerk is configured, you can manage your profile, security and connected accounts here. You can still explore the whole campus.',
      offlineAuthBody:
        'This preview runs without active Clerk keys. You can still read all lessons, resources and posts - progress is stored locally in your browser.',
      toHome: 'Back to home',
      viewCourses: 'View courses',
    },
    fundamentals: {
      eyebrow: 'Fundamentals',
      title: 'Tech Fundamentals for Building with AI',
      intro:
        'Plain-language basics every beginner runs into: Node.js, the terminal, Git, APIs, .env files, DNS and more. Each page gives you a clear definition, quick setup steps and the common confusions, with links straight into the lessons that use them.',
      toCourses: 'Start the courses',
      toKnowledge: 'Read the guides',
      minRead: 'min read',
      breadcrumb: 'Fundamentals',
      inThisPage: 'On this page',
      definition: 'In short',
      faqHeading: 'Frequently asked questions',
      relatedHeading: 'Keep learning',
      relatedFundamentals: 'Related fundamentals',
      relatedLessons: 'Lessons that use this',
      fundamentalTag: 'Fundamental',
      lessonTag: 'Lesson',
      open: 'Open',
      updated: 'Updated',
      notFound: 'Fundamental not found',
      categories: {
        concepts: 'Concepts',
        tools: 'Tools and setup',
        workflow: 'Workflow',
      },
    },
    builds: {
      eyebrow: 'Builds',
      title: 'Builds: Real Projects, Honest Lessons',
      intro:
        'Case studies from the 20+ products I have shipped while learning to build with AI agents. Each one distils a single transferable lesson - API-first, validation, human in the loop, image-to-data pipelines - with no vanity metrics, just what I actually learned and would do again.',
      toAbout: 'Meet the founder',
      toCourses: 'Start the courses',
      minRead: 'min read',
      breadcrumb: 'Builds',
      stackLabel: 'Stack',
      lessonsHeading: 'Lessons learned',
      storyHeading: 'The story',
      relatedHeading: 'Keep learning',
      lessonTag: 'Lesson',
      fundamentalTag: 'Fundamental',
      open: 'Open',
      updated: 'Updated',
      statusLabel: { live: 'Live', internal: 'Internal', archived: 'Archived' },
      notFound: 'Build not found',
      inThisPage: 'On this page',
      categories: {
        saas: 'SaaS products',
        automation: 'Automations',
        tool: 'Tools',
        content: 'Content and media',
      },
    },
    glossary: {
      eyebrow: 'AI glossary',
      title: 'The AI and Agent Glossary',
      intro:
        'Clear, plain-language definitions of the AI and agent vocabulary you keep running into: AI agent, agentic AI, MCP, vibe coding, context window and more. Each entry answers the question directly, then goes one level deeper, with links into the guides and lessons that use the term.',
      toGuides: 'Read the guides',
      toFundamentals: 'See the tech fundamentals',
      minRead: 'min read',
      breadcrumb: 'Glossary',
      inThisPage: 'On this page',
      definition: 'In short',
      faqHeading: 'Frequently asked questions',
      relatedHeading: 'Related',
      relatedTerms: 'Related terms',
      relatedGuides: 'Guides that use this',
      relatedFundamentals: 'Related fundamentals',
      termTag: 'Term',
      guideTag: 'Guide',
      fundamentalTag: 'Fundamental',
      open: 'Open',
      updated: 'Updated',
      notFound: 'Term not found',
    },
    compare: {
      eyebrow: 'Comparisons',
      title: 'Compare the Tools, Honestly',
      intro:
        'Current, no-hype comparisons of AI coding tools, agents, models and the modern app stack. Each page gives you a spec table, the honest pros and cons of every option and a clear verdict, so you can pick the right one for your work in minutes.',
      toGuides: 'Read the guides',
      toGlossary: 'Open the glossary',
      minRead: 'min read',
      breadcrumb: 'Comparisons',
      inThisPage: 'On this page',
      atAGlance: 'At a glance',
      optionsHeading: 'The options',
      specHeading: 'Side by side',
      bestForLabel: 'Best for',
      prosLabel: 'Strengths',
      consLabel: 'Trade-offs',
      verdictHeading: 'The verdict',
      dimensionLabel: 'Dimension',
      faqHeading: 'Frequently asked questions',
      relatedHeading: 'Related',
      relatedGuides: 'Related guides',
      relatedComparisons: 'Other comparisons',
      relatedTerms: 'Related terms',
      guideTag: 'Guide',
      comparisonTag: 'Comparison',
      termTag: 'Term',
      open: 'Open',
      updated: 'Updated',
      notFound: 'Comparison not found',
    },
    guides: {
      eyebrow: 'Guides',
      title: 'Guides for Agentic Engineering',
      intro:
        'Practical, hands-on guides for builders: how to use Claude Code and its hooks, how to build agents and MCP servers, how to ship a SaaS with the modern stack and automate real work. Step-by-step where it counts, with examples, FAQ and links into the lessons.',
      toCourses: 'Start the courses',
      toGlossary: 'Open the glossary',
      minRead: 'min read',
      breadcrumb: 'Guides',
      inThisPage: 'On this page',
      stepsHeading: 'Step by step',
      faqHeading: 'Frequently asked questions',
      relatedHeading: 'Keep learning',
      relatedGuides: 'Related guides',
      relatedTerms: 'Related terms',
      relatedLessons: 'Lessons that go deeper',
      pillarLabel: 'Part of',
      guideTag: 'Guide',
      termTag: 'Term',
      lessonTag: 'Lesson',
      open: 'Open',
      updated: 'Updated',
      notFound: 'Guide not found',
    },
    tools: {
      eyebrow: 'Free tools',
      title: 'Free Tools for Builders',
      intro:
        'Free, no-signup interactive tools that run entirely in your browser: generate a spec-correct llms.txt, and more cost, token and decision helpers to come. Nothing you type leaves your machine, and every tool is paired with a plain-language explainer.',
      toGuides: 'Read the guides',
      toGlossary: 'Open the glossary',
      minRead: 'min read',
      breadcrumb: 'Tools',
      inThisPage: 'On this page',
      widgetHeading: 'The tool',
      aboutHeading: 'About this tool',
      faqHeading: 'Frequently asked questions',
      relatedHeading: 'Related',
      relatedGuides: 'Related guides',
      relatedTerms: 'Related terms',
      guideTag: 'Guide',
      termTag: 'Term',
      open: 'Open',
      updated: 'Updated',
      notFound: 'Tool not found',
    },
    toolWidgets: {
      llmsTxt: {
        nameLabel: 'Site name',
        namePlaceholder: 'Acme Docs',
        summaryLabel: 'One-line summary',
        summaryPlaceholder: 'What your site is and who it is for.',
        detailsLabel: 'Optional details',
        detailsPlaceholder: 'A sentence or two of extra context (optional).',
        sectionLabel: 'Section heading',
        sectionPlaceholder: 'Docs',
        linksLabel: 'Key pages',
        linkTitlePlaceholder: 'Page title',
        linkUrlPlaceholder: 'https://example.com/page',
        linkDescPlaceholder: 'Short description',
        addLink: 'Add a page',
        removeLink: 'Remove this page',
        outputTitle: 'Your llms.txt',
        copy: 'Copy',
        copied: 'Copied',
        download: 'Download',
      },
      costCalculator: {
        modelALabel: 'Model A',
        modelBLabel: 'Model B',
        inputTokensLabel: 'Input tokens per call',
        outputTokensLabel: 'Output tokens per call',
        inputWordsLabel: 'Input words per call',
        outputWordsLabel: 'Output words per call',
        callsLabel: 'Calls per month',
        unitLegend: 'Enter your usage as',
        unitTokens: 'Tokens',
        unitWords: 'Words',
        wordsHint: 'About {input} input and {output} output tokens.',
        optionsLegend: 'Cost-saving options',
        cachingLabel: 'Prompt caching',
        cachingHint:
          'Reuse a large prompt prefix at about a tenth of the input price.',
        batchLabel: 'Batch processing',
        batchHint: 'Run non-urgent jobs at about half price on both sides.',
        perCall: 'Cost per call',
        perMonth: 'Cost per month',
        inputCost: 'Input cost per call',
        outputCost: 'Output cost per call',
        rates: 'Rate per million',
        ratePair: '{input} in / {output} out',
        cheaperBadge: 'Cheaper',
        disclaimer:
          'Estimates use list prices as of 2026 and may not reflect current rates. Your real bill depends on your exact usage. This is a planning estimate, not a live quote.',
      },
      tokenEstimator: {
        textLabel: 'Paste your text',
        textPlaceholder: 'Paste a prompt, a document or a transcript here.',
        tokensLabel: 'Estimated tokens',
        charactersLabel: 'Characters',
        wordsLabel: 'Words',
        estimateNote:
          'This is an estimate using about four characters per token, not an exact tokenizer. Real counts vary by model and for code or non-English text.',
        fitHeading: 'Context window fit',
        windowOf: '{window} token window',
        fits: 'Fits',
        doesNotFit: 'Does not fit',
        approxCost: '~{cost} input',
        costNote: 'Want output tokens, monthly volume and discounts too?',
        costLink: 'Open the LLM Cost Calculator',
      },
      roiCalculator: {
        hoursLabel: 'Hours per week (per person)',
        peopleLabel: 'People doing the task',
        hourlyCostLabel: 'Hourly cost ({currency})',
        automatableLabel: 'Share automatable (%)',
        automatableHint: 'How much of the task an automation can take over.',
        setupHoursLabel: 'Setup effort (hours)',
        setupCostLabel: 'Extra setup cost ({currency})',
        monthlyToolCostLabel: 'Monthly tool cost ({currency})',
        netMonthlyLabel: 'Net saving per month',
        netMonthlyLossLabel: 'Net cost per month',
        hoursSavedLabel: 'Time saved',
        hoursSavedYearLabel: 'Time saved',
        perMonthUnit: ' h / month',
        perYearUnit: ' h / year',
        grossMonthlyLabel: 'Gross saving / month',
        annualNetLabel: 'Net saving / year',
        setupInvestmentLabel: 'One-time investment',
        firstYearNetLabel: 'Net in year one',
        paybackLabel: 'Payback period',
        paybackValue: '{months} months',
        noPayback: 'Does not pay back',
        roiLabel: 'First-year ROI',
        notAvailable: 'n/a',
        basisNote:
          'Monthly figures use {weeks} weeks per month. Setup hours are valued at the hourly cost.',
        disclaimer:
          'A planning estimate, not a guarantee. Real savings depend on adoption and how well the automation holds up. Learn more:',
        guideLink: 'AI automation for business',
        comparisonLink: 'n8n vs Zapier vs Make',
        linkSeparator: ' and the ',
        glossaryLink: 'workflow automation glossary',
      },
      codingQuiz: {
        progressLabel: 'Question {current} of {total}',
        back: 'Back',
        reset: 'Start over',
        seeResult: 'See my result',
        resultEyebrow: 'Your match',
        runnerUpLabel: 'Also worth a look: {tool}.',
        seeComparison: 'Read the {tool} comparison',
        changeLast: 'Change last answer',
        restart: 'Retake the quiz',
        questions: {
          environment: {
            title: 'Where do you want to work with the AI?',
            options: {
              terminal: 'In the terminal / command line',
              ide: 'Inside my code editor (IDE)',
              either: 'Either is fine',
            },
          },
          autonomy: {
            title: 'How much should the AI do on its own?',
            options: {
              autonomous: 'Run multi-step tasks end to end',
              collaborative: 'Work with me step by step',
              autocomplete: 'Mostly autocomplete as I type',
            },
          },
          experience: {
            title: 'How experienced are you as a developer?',
            options: {
              beginner: 'New to coding',
              intermediate: 'Comfortable, still learning',
              advanced: 'Experienced engineer',
            },
          },
          budget: {
            title: 'What is your budget?',
            options: {
              free: 'Free or open source only',
              value: 'Good value, a small monthly fee',
              premium: 'Whatever gets the best results',
            },
          },
          codebase: {
            title: 'How big is the codebase you work in?',
            options: {
              small: 'Small scripts or a single file',
              medium: 'A normal app or project',
              large: 'A large or complex codebase',
            },
          },
          useCase: {
            title: 'What will you mostly use it for?',
            options: {
              features: 'Building new features',
              refactor: 'Refactoring and fixing code',
              learning: 'Learning while I build',
              scripts: 'Quick scripts and one-off tasks',
            },
          },
          openSource: {
            title: 'How much does open source matter to you?',
            options: {
              must: 'It must be open source',
              prefer: 'I prefer open source',
              dontcare: 'I do not mind either way',
            },
          },
        },
        tools: {
          'claude-code': {
            name: 'Claude Code',
            rationale:
              'A terminal-first coding agent that runs multi-step tasks end to end, handles large codebases and big refactors, and works as a reliable teammate. The strongest all-round pick when you want real autonomy.',
          },
          cursor: {
            name: 'Cursor',
            rationale:
              'An AI-native code editor that keeps you in the loop while it edits, with strong autocomplete and chat. A great balance of power and control, especially if you like working inside an IDE.',
          },
          'codex-cli': {
            name: 'Codex CLI',
            rationale:
              'A terminal coding agent that is at home with scripts, automation and command-line workflows. A solid pick for developers who live in the shell and want capable autonomous help.',
          },
          'github-copilot': {
            name: 'GitHub Copilot',
            rationale:
              'The classic, affordable autocomplete assistant built into your editor. Ideal when you mainly want fast inline suggestions while you write and learn, without a steep cost.',
          },
          windsurf: {
            name: 'Windsurf',
            rationale:
              'An agentic IDE that collaborates closely on medium-to-large projects, sitting between pure autocomplete and full autonomy. A good fit if you want an editor that plans with you.',
          },
          aider: {
            name: 'Aider',
            rationale:
              'A free, open-source coding agent that runs in the terminal and works through Git. The natural pick when open source is a must-have and you want full control over the model and your repo.',
          },
        },
      },
    },
    about: {
      eyebrow: 'About',
      title: 'The Founder Behind Agentic School',
      intro:
        'I am Nico. I have shipped more than twenty products by building with AI agents - and I built this campus to teach the exact workflow that made it possible, honestly and without the hype.',
      toBuilds: 'See the builds',
      toCourses: 'Start the courses',
      bioHeading: 'Who I am',
      bio: [
        'I am a founder and builder based in Switzerland. I did not come up through a traditional engineering path - I learned to ship by pairing relentless curiosity with AI coding agents, and by being honest with myself about what actually worked and what did not.',
        'Everything on this campus is something I do myself, in real projects. I am not teaching theory from a textbook. I am writing down the workflow I use every single day to turn an idea into a deployed product faster than I ever could alone.',
      ],
      journeyHeading: 'The journey: 20+ products',
      journey: [
        'I have built API-first business tools, phone agents on Twilio, image-to-data pipelines for invoices and trading cards, browser automations, audio and vision tools, and more. Some are live, some stayed internal, some I archived once they had taught me what I needed.',
        'The pattern across all of them is the same: ship something real, watch where it breaks, and keep the lesson. The builds below are the honest record of that, metrics-free and useful.',
      ],
      philosophyHeading: 'How I think',
      philosophy: [
        {
          title: 'Agent-first',
          body: 'I design tools so an AI can operate them, not just a human. The API comes before the UI, and a machine-readable contract is a feature.',
        },
        {
          title: 'Continuous learning',
          body: 'Every failure becomes a written rule, a test, or a skill my agents reuse. The system gets smarter because I write down what I learn.',
        },
        {
          title: 'Exponential thinking',
          body: 'The tools double in capability every few months. I bet on where the curve is heading, not on where it is today.',
        },
      ],
      projectsHeading: 'Every build, in one place',
      projectsLead:
        'A complete grid of the projects above, grouped by what they are. Each links to its full case study.',
      statusLabel: { live: 'Live', internal: 'Internal', archived: 'Archived' },
      categories: {
        saas: 'SaaS products',
        automation: 'Automations',
        tool: 'Tools',
        content: 'Content and media',
      },
      imageAlt: 'Portrait of Nico, founder of Agentic School',
    },
    apiDocs: {
      eyebrow: 'For agents',
      title: 'API and agent access',
      intro:
        'Agentic School is built agent-first. Every page is available as clean Markdown, the full catalog as a read-only JSON API, and the whole site as llms.txt - no key, no sign-up, free to use.',
      conventions: {
        title: 'Conventions',
        items: [
          'Append .md to any content URL to get clean Markdown with frontmatter (title, description, locale, canonical, dates). Example: /courses/foundations.md',
          'The read-only JSON API lives under /api/v1. German content is served under /api/v1/de.',
          'Discover everything machine-readable from /llms.txt (English) or /de/llms.txt (German); /llms-full.txt is the full corpus in one document.',
          'Every JSON payload carries a stable schema, a locale field and canonical site URLs.',
        ],
      },
      endpointsTitle: 'Endpoints',
      examplesTitle: 'Examples',
      markdownTitle: 'Markdown for any page',
      markdownBody:
        'Every course, lesson, knowledge article, fundamental, build, resource and changelog entry has a Markdown twin at its human URL plus a .md suffix, in both locales. Content pages also advertise it with a <link rel="alternate" type="text/markdown"> tag.',
      fairUseTitle: 'Fair use',
      fairUseBody:
        'This surface is fully static and served from a CDN, so there are no hard rate limits. Please cache responses, prefer the JSON and .md endpoints over scraping HTML, identify your agent with a descriptive User-Agent, and use /llms-full.txt for a one-shot full corpus instead of crawling every page. A courtesy ceiling of around 60 requests per minute keeps the surface healthy for everyone.',
      linksTitle: 'Quick links',
      ep: {
        discovery: 'API discovery document with every endpoint',
        openapi: 'OpenAPI 3.1 spec for the whole API',
        courses: 'List all courses with their lessons',
        course: 'A single course with full lesson list',
        lessons: 'All lessons as metadata',
        lesson: 'Full detail for a single lesson',
        fundamentals: 'All beginner reference pages',
        articles: 'All knowledge articles',
        builds: 'All founder project case studies',
        resources: 'All resources with full content',
        changelog: 'All tool-news changelog entries',
        searchIndex: 'Flat search index for client or agent search',
      },
    },
  },
} as const satisfies Dictionary
